import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { createApiUrl } from '../utils/apiConfig';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(createApiUrl('/auth/check'), {
        withCredentials: true
      });
      
      if (response.data.user) {
        setUser(response.data.user);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('Auth check failed:', error);
      }
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(createApiUrl('/auth/login'), { username: email, password }, {
        withCredentials: true
      });
  
      const authCheck = await axios.get(createApiUrl('/auth/check'), {
        withCredentials: true
      });
      
      const user = authCheck.data.user;
      
      setUser(user);
      setIsAuthenticated(true);
      return { success: true, user, redirect: response.data.redirect };
    } catch (error) {
      console.error('🔍 AuthContext login - Error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(createApiUrl('/auth/register'), userData, {
        withCredentials: true
      });
      setUser(response.data.user);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed'
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(createApiUrl('/logout'), {}, {
        withCredentials: true
      });
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      
    } catch (error) {
      console.error('Logout failed:', error);
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    register,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 